import React, { useState, useMemo, useEffect } from "react";
import { Stack, Typography, Box, Button, Modal, Slide, OutlinedInput, RadioGroup, Radio, IconButton } from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as MoveIcon } from "../../../../vendor/_new/icon/project/move.svg";
import { ReactComponent as ProjectIcon } from "../../../../vendor/_new/icon/navbar/project.svg";
import { ReactComponent as SearchIcon } from "../../../../vendor/_new/icon/navbar/search.svg";

import colors from "../../../../utils/colors";
import useSite from "../../../../hooks/user/useSite";
import { CancelButton, containedButtonSx } from "../../../common/Button";
import { generateMenuSx } from "../../../../layouts/_new/Normal";

const ProjectMoveModal = ({ open, projectToMove, handleClose, getProjectList, allProjects }) => {
  const { moveProject } = useSite();

  const filteredProjectList = useMemo(() => {
    return allProjects?.filter((project) => project?.id !== projectToMove?.id)?.filter((project) => !project?.is_sub);
  }, [allProjects, projectToMove]);

  useEffect(() => {
    if (filteredProjectList?.length === 0) setErr("There are no target projects.");
  }, [filteredProjectList]);

  const [pattern, setPattern] = useState("");
  const [err, setErr] = useState("");
  const [isMoving, setIsMoving] = useState(false);
  const [targetProjectId, setTargetProjectId] = useState("");

  useEffect(() => {
    setTargetProjectId("");
    setErr("");
    setIsMoving(false);
    setPattern("");
  }, [open]);

  const handleMove = async () => {
    if (!targetProjectId) {
      setErr("Please select target project");
      return;
    }
    setIsMoving(true);
    try {
      await moveProject(projectToMove?.id, targetProjectId);
      await getProjectList();
    } catch (err) {
      setErr(err || err?.message || "Something went wrong");
    }
    setIsMoving(false);
    handleClose();
  };

  return (
    <Modal 
      open={open} 
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }
      }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            height: "100vh",
            width: "33%",
            minWidth: 480,
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
          }}
        >
          <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
            <Stack>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <MoveIcon style={{ color: colors.neutral[1000] }} />
                <Typography
                  sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                >
                  Move Project
                </Typography>
              </Stack>
              <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700], pl: 7 }}>
                {projectToMove?.name}
              </Typography>
            </Stack>
            <IconButton onClick={handleClose} aria-label="Close" sx={{ p: 0 }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </IconButton>
          </Stack>
          <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 118px)` }}>
            <ReactPerfectScrollbar>
              <Stack spacing={4} justifyContent={"space-between"} sx={{ height: "100%", pr: 4 }}>
                <Stack>
                  <OutlinedInput
                    fullWidth
                    startAdornment={<SearchIcon style={{ color: colors.neutral[600] }} />}
                    placeholder="Search project by name"
                    value={pattern}
                    onChange={(e) => setPattern(e.target.value)}
                    sx={{
                      mb: 3,
                      height: 32,
                      borderRadius: "8px",
                      fontSize: 14,
                      lineHeight: "21px",
                      borderColor: colors.neutral[600],
                      pl: 2,
                      "& .MuiOutlinedInput-input": {
                        fontSize: 14,
                        lineHeight: "21px",
                        px: 2,
                      },
                      "& .MuiInputBase-input::placeholder": {
                        fontSize: 14,
                        lineHeight: "21px",
                      },
                    }}
                  />
                  <RadioGroup value={targetProjectId}>
                    {filteredProjectList
                      ?.filter((project) => project?.name?.toLowerCase()?.includes(pattern?.toLowerCase()))
                      ?.map((project) => (
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          onClick={() => {
                            setTargetProjectId(project?.id);
                            setErr("");
                          }}
                          sx={generateMenuSx("100%", 37)}
                        >
                          <Stack direction={"row"} alignItems={"center"} spacing={1}>
                            <ProjectIcon />
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                              {project?.name}
                            </Typography>
                          </Stack>
                          <Radio
                            value={project?.id}
                            size="small"
                            sx={{
                              color: colors.neutral[300],
                              "&.Mui-checked": {
                                color: `${colors.secondary[300]}!important`,
                              },
                            }}
                          />
                        </Stack>
                      ))}
                  </RadioGroup>
                  {err && (
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      spacing={2}
                      sx={{
                        minHeight: 48,
                        p: 2,
                        borderRadius: "8px",
                        backgroundColor: colors.red[10],
                        border: `solid 1px ${colors.red[100]}`,
                      }}
                    >
                      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.red[100] }}>{err}</Typography>
                    </Stack>
                  )}
                </Stack>
                <Stack direction={"row"} alignItems={"center"} spacing={3}>
                  <Button disabled={isMoving} onClick={handleMove} sx={{ height: 44, width: "50%", ...containedButtonSx }}>
                    Move
                  </Button>
                  <CancelButton onClick={handleClose} />
                </Stack>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>
        </Stack>
      </Slide>
    </Modal>
  );
};
export default ProjectMoveModal;
